import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiCode, FiExternalLink, FiHome, FiMapPin, FiPhone, FiShare2, FiClock } from 'react-icons/fi';
import { toast } from '../../Toast';
import { SettingsActions } from '../../clientSettings/ClientSettings';
import { BackLink, HubHeader, hubPath, useHubSite } from '../shared';

interface Form {
    name: string;
    ticker: string;
    abn: string;
    acn: string;
    street: string;
    suburb: string;
    state: string;
    postcode: string;
    postal: string;
    phone: string;
    email: string;
    website: string;
    ir: string;
    registry: string;
    registryPhone: string;
    linkedin: string;
    x: string;
    youtube: string;
}

type Key = keyof Form;

// one input; label + optional grey "(optional)"
const Field: React.FC<{ k: Key; label: string; optional?: boolean; form: Form; set: (k: Key, v: string) => void }> = ({ k, label, optional, form, set }) =>
    <label className="field">
        <span className="label">{label}{optional && <em>(optional)</em>}</span>
        <input value={form[k]} onChange={e => set(k, e.target.value)} />
    </label>;

const Section: React.FC<{ icon: React.ReactNode; title: string; hint: string }> = ({ icon, title, hint, children }) =>
    <section className="card">
        <div className="section-title">
            <span className="icon-tile">{icon}</span>
            <div className="text"><h2>{title}</h2><div className="hint">{hint}</div></div>
        </div>
        {children}
    </section>;

// sample log; no change history is stored yet
const CHANGES = [
    { what: 'Phone number updated', when: '3 Mar 2026', who: 'Wallace Turner' },
    { what: 'Office moved to Churchill Avenue', when: '18 Nov 2025', who: 'Rob Fletcher' },
    { what: 'Details first added', when: '9 Sep 2025', who: 'Investi setup' },
];

// Website Hub › Website content › Company details (spec 406:10274)
const CompanyDetails: React.FC = () => {
    const history = useHistory();
    const { host, name, short, ticker, url } = useHubSite();
    // "49metals" for the social handles
    const handle = host.split('.')[0];
    const initial: Form = {
        name, ticker, abn: '12 345 678 901', acn: '345 678 901',
        street: '6, 245 Churchill Avenue', suburb: 'Subiaco', state: 'WA', postcode: '6008', postal: 'Same as registered office',
        phone: '+61 400 252 465', email: `info@${host}`, website: `https://www.${host}`, ir: `Tom Blanchi — tom@${host}`,
        registry: 'Automic Group', registryPhone: '1300 288 664',
        linkedin: `linkedin.com/company/${handle}`, x: `x.com/${short.replace(/\s+/g, '')}`, youtube: `youtube.com/@${handle}`,
    };
    const [form, setForm] = useState<Form>(initial);
    const [saved, setSaved] = useState<Form>(initial);
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    // the client loads after the first render on a cold start: refill the form once its name arrives
    useEffect(() => {
        setForm(initial);
        setSaved(initial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, ticker, host]);

    const set = (k: Key, v: string) => setForm(f => ({ ...f, [k]: v }));
    const save = () => {
        setSaved(form);
        toast.info('Preview only: this isn’t connected yet, so the company details were not saved');
    };
    const actions = { dirty, onSave: save, onDiscard: () => setForm(saved) };
    const f = { form, set };

    return <>
        <BackLink to={hubPath('content')} label="Back to Website content" />
        <HubHeader title="Company Details" sub="Address and contact details used in the website footer, Corporate directory and email footers">
            <button type="button" className="btn-outline with-icon" onClick={() => history.push('/client-settings/widgets/company-details')}><FiCode /> Widget Settings</button>
            <a className="btn-outline with-icon" href={url('/investors/corporate-directory/')} target="_blank" rel="noopener noreferrer"><FiExternalLink /> View on Website</a>
            <SettingsActions {...actions} />
        </HubHeader>

        <div className="cs-row">
            <div className="cs-main">
                <Section icon={<FiHome />} title="Company" hint="Legal name and identifiers">
                    <div className="fields">
                        <Field k="name" label="Company name" {...f} />
                        <Field k="ticker" label="ASX code" {...f} />
                        <Field k="abn" label="ABN" {...f} />
                        <Field k="acn" label="ACN" {...f} />
                    </div>
                </Section>

                <Section icon={<FiMapPin />} title="Registered office" hint="Shown in the website footer">
                    <Field k="street" label="Street address" {...f} />
                    <div className="fields three">
                        <Field k="suburb" label="Suburb" {...f} />
                        <Field k="state" label="State" {...f} />
                        <Field k="postcode" label="Postcode" {...f} />
                    </div>
                    <Field k="postal" label="Postal address" optional {...f} />
                </Section>

                <Section icon={<FiPhone />} title="Contact" hint="Where investors can reach you">
                    <div className="fields">
                        <Field k="phone" label="Phone" {...f} />
                        <Field k="email" label="Email" {...f} />
                        <Field k="website" label="Website" {...f} />
                        <Field k="ir" label="Investor relations contact" {...f} />
                    </div>
                </Section>

                <Section icon={<FiShare2 />} title="Share registry & social" hint="Shown on the Corporate directory page">
                    <div className="fields">
                        <Field k="registry" label="Share registry" {...f} />
                        <Field k="registryPhone" label="Registry phone" {...f} />
                    </div>
                    <div className="fields three">
                        <Field k="linkedin" label="LinkedIn" {...f} />
                        <Field k="x" label="X (Twitter)" {...f} />
                        <Field k="youtube" label="YouTube" optional {...f} />
                    </div>
                </Section>
            </div>

            <div className="cs-side hub-side">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiClock /></span>
                        <div className="text"><h2>Last changes</h2></div>
                    </div>
                    <div className="hub-changes">
                        {CHANGES.map(c => <div key={c.what}><strong>{c.what}</strong><span>{c.when} · {c.who}</span></div>)}
                    </div>
                </section>
            </div>
        </div>

        {/* mobile: Discard / Save move from the header to the end of the page (Account.scss .action-top / .action-bottom) */}
        <div className="action-bottom hub-action-bottom"><SettingsActions {...actions} /></div>
    </>;
};

export default CompanyDetails;
